// TypeScript types mirroring the Phase-3 backend shapes (migration 0009 +
// http/v1.rs reader-production endpoints). These describe the JSON the reader
// components consume; see reader/api.ts for the typed client that returns them.

// ── Core content ──────────────────────────────────────────────────────────

/** One furigana/pinyin (or null for punctuation/latin/whitespace) token aligned
 *  into a chunk's `source_text` via character offset. */
export interface RubyToken {
  /** Surface form as it appears in source_text. */
  s: string;
  /** Reading (furigana / pinyin). null = punctuation, latin, or whitespace. */
  r: string | null;
  /** Character offset of `s` into the chunk's source_text. */
  off: number;
}

/** A single reader chunk. Re-exported alias over the existing book-chunk shape
 *  so reader components have a local, stable name. */
export interface Chunk {
  id: string;
  book_id: string;
  chunk_index: number;
  source_text: string;
  source_language: string | null;
  translations: Translation[];
  tts_assets: TtsAsset[];
  /** Optional pronunciation tokens for source_text (furigana / pinyin). */
  annotations?: RubyToken[] | null;
  created_at: string;
}

export interface Translation {
  id: string;
  book_chunk_id: string;
  target_language: string;
  translated_text: string;
  provider: string | null;
  created_at: string;
}

/** One forced-alignment word timing (heuristic or Deepgram). */
export interface WordTiming {
  word: string;
  start_ms: number;
  end_ms: number;
}

export interface TtsAsset {
  id: string;
  book_chunk_id: string;
  language: string;
  uri: string;
  content_type: string;
  duration_ms: number | null;
  provider: string | null;
  /** Per-word timings for karaoke. Absent/empty until alignment runs. */
  word_timings?: WordTiming[] | null;
  created_at: string;
}

// ── Structure (GET /books/:id/chapters, /images) ───────────────────────────

export interface Chapter {
  id: string;
  book_id: string;
  ordinal: number;
  level: number;
  title: string;
  start_chunk_index: number;
  created_at: string;
}

export interface ImageBlock {
  id: string;
  book_id: string;
  after_chunk_index: number;
  /** Relative storage uri, e.g. `books/<book_id>/images/<name>`. Resolve via
   *  the backend image route before using as an <img> src. */
  uri: string;
  width: number | null;
  height: number | null;
  caption: string | null;
  bbox: unknown | null;
  ordinal: number;
  created_at: string;
}

// ── Annotations (bookmarks / highlights) ───────────────────────────────────

export interface Bookmark {
  id: string;
  book_id: string;
  chunk_index: number;
  char_offset: number;
  label: string | null;
  created_at: string;
}

export interface Highlight {
  id: string;
  book_id: string;
  chunk_index: number;
  start_offset: number;
  end_offset: number;
  color: string | null;
  note: string | null;
  created_at: string;
}

// ── Reader preferences (GET/PUT /me/reader-prefs `settings` blob) ──────────

export type KaraokeMode = "off" | "word" | "sentence";
export type TtsGenerationMode = "full" | "lazy" | "off";
export type ViewMode = "target" | "source" | "dual";

/** Strongly-typed view of the reader_prefs `settings` JSON object. Every field
 *  is optional on the wire (whole-document replace, defaults applied client
 *  side); use DEFAULT_READER_PREFS to fill gaps. */
export interface ReaderPrefs {
  /** Theme id from reader/themes.ts THEMES. */
  theme: string;
  fontFamily: string;
  fontSize: number;
  /** line-height multiplier. */
  lineSpread: number;
  justify: boolean;
  hyphenate: boolean;
  /** horizontal margin preset in px (per side of the text column). */
  margin: number;
  karaokeMode: KaraokeMode;
  ttsGenerationMode: TtsGenerationMode;
  viewMode: ViewMode;
  /** Show furigana/pinyin ruby guides when chunks carry annotations. */
  ruby: boolean;
  /**
   * App shell theme (`paper` | `ink`). Owned by AppThemeProvider; included in
   * the whole-doc prefs blob so it syncs cross-device. Reader text themes use
   * `theme` above — do not conflate the two.
   */
  appTheme?: string;
}

export const DEFAULT_READER_PREFS: ReaderPrefs = {
  theme: "lumi",
  fontFamily: "serif",
  fontSize: 19,
  lineSpread: 1.75,
  justify: false,
  hyphenate: false,
  margin: 28,
  karaokeMode: "sentence",
  ttsGenerationMode: "lazy",
  viewMode: "target",
  ruby: false,
};

/** Merge a partial/unknown settings blob from the API over the defaults. */
export function normalizeReaderPrefs(
  raw: Partial<ReaderPrefs> | null | undefined,
): ReaderPrefs {
  return { ...DEFAULT_READER_PREFS, ...(raw ?? {}) };
}
